import { apiUrl } from "./config";

const parsing = "/pdf/template-parsing";
const figures = "/pdf/material-figures";
const errorText = (value) => typeof value === "string" ? value
  : Array.isArray(value) ? value.map(errorText).filter(Boolean).join("；")
    : value && typeof value === "object" ? Object.values(value).map(errorText).find(Boolean) || "" : "";

export function materialServiceUrl(path, apiBase = apiUrl, origin = window.location.origin) {
  const base = new URL(apiBase, origin);
  const target = /^https?:\/\//.test(path) ? path
    : `${apiBase}${path.startsWith("/api/") ? path.slice(4) : path}`;
  const resolved = new URL(target, origin);
  if (resolved.origin !== base.origin) throw new Error("圖片來源與教材服務不一致。");
  return resolved.href;
}

// Mutations are never automatically repeated: a timeout may still have been accepted.
export async function materialRequest(path, { authToken, method = "GET", data, signal, blob = false, keepalive = false } = {}) {
  if (!authToken) throw new Error("請先登入。");
  const controller = new AbortController();
  const abort = () => controller.abort();
  if (signal?.aborted) abort();
  else signal?.addEventListener("abort", abort, { once: true });
  const timer = setTimeout(abort, 90000);
  try {
    const headers = { Authorization: `Bearer ${authToken}` };
    const form = data instanceof FormData;
    if (data !== undefined && !form) headers["Content-Type"] = "application/json";
    const url = materialServiceUrl(path);
    const response = await fetch(url, { method, headers, signal: controller.signal, keepalive,
      body: data === undefined ? undefined : form ? data : JSON.stringify(data) });
    if (blob && response.ok) return response.blob();
    const result = await response.json().catch(() => ({}));
    if (!response.ok || result.success !== true) {
      const error = new Error(errorText(result.error || result.detail || result) || "教材服務回應不完整，請查詢接收狀態。");
      if (response.ok && method !== "GET") error.uncertain = true;
      error.status = response.status; error.data = result; throw error;
    }
    return result;
  } catch (error) {
    if (!error.status && method !== "GET") error.uncertain = true;
    if (error.name === "AbortError") {
      const timeout = new Error("連線中斷或等候逾時，請先查詢工作狀態。");
      timeout.uncertain = method !== "GET"; throw timeout;
    }
    throw error;
  } finally {
    clearTimeout(timer); signal?.removeEventListener("abort", abort);
  }
}

export const listMaterialTemplates = (options) => materialRequest(`${parsing}/templates/`, options);
export const listMaterialJobs = (options) => materialRequest(`${parsing}/jobs/`, options);
export const findMaterialReceipt = (id, options) => materialRequest(`${parsing}/jobs/?client_request_id=${encodeURIComponent(id)}`, options);
export const getMaterialJob = (id, options) => materialRequest(`${parsing}/jobs/${id}/`, options);
export const saveMaterialDocument = (id, document, revision, options) => materialRequest(`${parsing}/jobs/${id}/`, { ...options, method: "PATCH", data: { document, revision } });
export const materialJobAction = (id, action, options) => materialRequest(`${parsing}/jobs/${id}/${action}/`, { ...options, method: "POST", data: options?.data || {} });
export const startMaterialJob = ({ file, templateId, clientRequestId, ...options }) => {
  const data = new FormData();
  data.append("file", file); data.append("template_id", templateId);
  data.append("client_request_id", clientRequestId); data.append("quality", "ultra");
  return materialRequest(`${parsing}/jobs/`, { ...options, method: "POST", data });
};
export const listMaterialFigures = (documentId, { page = 1, pageSize = 24, jobsPage = 1, jobsPageSize = 24, ...options } = {}) => {
  const query = new URLSearchParams({ document_id: documentId, page, page_size: pageSize, jobs_page: jobsPage, jobs_page_size: jobsPageSize });
  return materialRequest(`${figures}/?${query}`, options);
};
export const getMaterialFigure = (id, cropRevision, options) => materialRequest(`${figures}/${id}/?include_code=1&crop_revision=${encodeURIComponent(cropRevision)}`, options);

// Selection walks bounded metadata pages. Python and image bytes are never part of it.
const materialFigureSelection = async (documentId, kind, { status, ...options } = {}) => {
  const items = new Map();
  let cursor;
  const cursors = new Set();
  while (true) {
    const query = new URLSearchParams({ document_id: documentId, kind, page_size: 500 });
    if (status) query.set("status", status);
    if (cursor) query.set("cursor", cursor);
    const result = await materialRequest(`${figures}/selection/?${query}`, options);
    for (const item of result.items || []) items.set(item.id, item);
    if (!result.pagination?.has_next) return [...items.values()];
    const nextCursor = result.pagination.next_cursor;
    if (!result.items?.length || !nextCursor || cursors.has(nextCursor)) throw new Error("附圖清單尚未完整載入，請重新整理後再試。");
    cursors.add(nextCursor); cursor = nextCursor;
  }
};
export const listMaterialFigureReferences = async (documentId, options) => ({ figures: await materialFigureSelection(documentId, "figures", options) });
export const listMaterialFigureJobs = async (documentId, options) => ({ jobs: await materialFigureSelection(documentId, "jobs", options) });
export const detectMaterialFigures = (data, options) => materialRequest(`${figures}/detect/`, { ...options, method: "POST", data });
export const createMaterialFigure = (data, options) => materialRequest(`${figures}/`, { ...options, method: "POST", data });
export const updateMaterialFigure = (id, data, options) => materialRequest(`${figures}/${id}/`, { ...options, method: "PATCH", data });
export const enqueueMaterialFigures = (data, options) => materialRequest(`${figures}/enqueue/`, { ...options, method: "POST", data });
export const materialFigureJobAction = (id, action, data, options) => materialRequest(`${figures}/jobs/${id}/${action}/`, { ...options, method: "POST", data });
export const materialFigureAction = (id, action, data, options) => materialRequest(`${figures}/${id}/${action}/`, { ...options, method: "POST", data });
export const materialImage = (url, options) => materialRequest(url, { ...options, blob: true });
